import type { Ref } from 'react'

import { TEDDIES, teddyPhotoPath, type TeddyKey } from '@hazel/shared'

import { fallBackToDrawing } from '../components/teddyPicture.js'
import { colourFor, markingFor } from './sudokuLayout.js'

export interface SudokuCellProps {
  readonly cell: number
  readonly columns: number
  /** Null for an empty square. */
  readonly value: number | null
  /** The teddy this value is drawn as, or null on the grid that uses numbers. */
  readonly teddy: TeddyKey | null
  /** Part of the puzzle. Not hers to change, and drawn so that it looks it. */
  readonly given: boolean
  /** Duplicates another in its row, column or box. Never "differs from the answer". */
  readonly conflicting: boolean
  /** Where the keyboard is pointing, if it is pointing here. */
  readonly cursor: boolean
  /** The heavy rules between boxes, from the layout table. */
  readonly edges: string
  /** How big to draw the value, from the layout table. */
  readonly valueClassName: string
  readonly disabled: boolean
  readonly ref?: Ref<HTMLButtonElement>
  readonly onPick: (element: HTMLButtonElement | null) => void
}

/**
 * One square.
 *
 * Always a button, filled or empty, so every square is reachable and announced
 * rather than being a silent div — and so a finger works as well as a keyboard.
 *
 * **Named for what is in it, not for the number underneath.** "Row 2, column 3,
 * Sky" rather than "cell 12, 4". The number is an implementation detail of the
 * engine and means nothing to anybody looking at a grid of teddies.
 *
 * A given square is a button too, and disabled. Drawing it as plain text would
 * be tidier and would make the grid uneven to arrow through — the cursor would
 * skip over the squares that are the puzzle, which is disorienting in a game
 * where reading across a row is the whole activity. So she can put the cursor
 * anywhere; the ones she may not change simply refuse.
 */
export function SudokuCell({
  cell,
  columns,
  value,
  teddy,
  given,
  conflicting,
  cursor,
  edges,
  valueClassName,
  disabled,
  ref,
  onPick,
}: SudokuCellProps) {
  const row = Math.floor(cell / columns) + 1
  const column = (cell % columns) + 1
  const meta = teddy ? TEDDIES[teddy] : undefined

  const what = meta ? meta.name : value === null ? 'empty' : String(value)
  const label = [
    `Row ${row}, column ${column}`,
    what,
    given ? 'given' : null,
    // Said aloud as well as shown, because the colour alone is no use to
    // somebody who cannot see it and this is the game's only feedback.
    conflicting ? 'clashes' : null,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <button
      type="button"
      ref={ref}
      // One tab stop for the whole grid, not eighty-one. Tab should land where
      // the cursor already is, which is the same rule Sky's board follows.
      tabIndex={cursor ? 0 : -1}
      aria-label={label}
      disabled={disabled}
      onClick={(event) => {
        onPick(event.currentTarget)
      }}
      style={{ touchAction: 'manipulation' }}
      className={[
        'relative flex aspect-square items-center justify-center overflow-hidden',
        'border-border border p-0.5 transition-colors',
        edges,
        given ? 'bg-surface-sunken' : 'bg-surface',
        // What this square says about itself — hers, or clashing, or nothing
        // at all. Decided in one place, and tested there.
        markingFor({ given, filled: value !== null, conflicting }),
        cursor ? 'ring-focus z-10 ring-4' : '',
        disabled && !given ? 'cursor-not-allowed' : '',
      ].join(' ')}
    >
      {meta ? (
        <img
          src={teddyPhotoPath(meta.key)}
          alt=""
          className="size-full object-contain"
          onError={(event) => fallBackToDrawing(event.currentTarget, meta.key)}
        />
      ) : (
        <span
          aria-hidden="true"
          className={[
            'font-display leading-none',
            valueClassName,
            /*
             * The number's own colour, the same one every time.
             *
             * Which squares are hers is said by the ring around them rather
             * than by the colour of the digit — it has to be, since a teddy
             * square has no digit to colour, and one rule for both grids is
             * one rule to keep right. Givens stay bold, so the puzzle still
             * reads as the puzzle.
             *
             * **A clash does not change it.** That was tried the other way
             * round first — the digit went coral — and it breaks the one rule
             * these colours have: a number is the same colour every time, so
             * that she can learn them. A 3 that turns coral when it clashes is
             * a 3 that is sometimes yellow and sometimes not, and the pattern
             * she was reading the row by comes apart exactly when she is
             * trying hardest to read it.
             *
             * The clash is said by the ring and the wash around the square
             * instead, which `markingFor` puts there and which say it more
             * loudly than a letter-sized change of hue ever did. It also has
             * to be said that way for a grid of teddies, where there is no
             * digit to recolour at all — so this is one rule for both grids
             * rather than two.
             */
            given ? 'font-bold' : '',
            value === null ? '' : colourFor(value),
          ].join(' ')}
        >
          {value ?? ''}
        </span>
      )}
    </button>
  )
}
